/**
 * Study-guide lead-capture popup — auto-open behaviour.
 *
 * 1:1 port of the legacy `Amri.StudyGuidePopup` module
 * (`aclsonline/AMRI.WebProject/Scripts/amri.studyGuidePopup.js`), which
 * auto-opened the study-guide signup lightbox on blog / education pages via a
 * timed trigger AND an exit-intent trigger, gated by a 30-day cookie so a
 * visitor is nagged at most once per month.
 *
 * v2 renders the modal inline (`_StudyGuideModal.cshtml`, element id
 * `amri-modal-study-guide`) on the article / press-news / news listing pages;
 * the manual "Get the Free Guide" CTA already opens it via the generic modal
 * helper. This module restores the MISSING auto-open so passive readers see
 * the popup — the lead capture DEV-content-blog-index-2 had lost by porting
 * click-to-open only.
 *
 * Legacy delay: appSettings `StudyGuidePopupDelayInMiliseconds = 30000`.
 */
import { openModal } from '../auth/login';

/** `data-modal-toggle` / `data-modal-id` value (openModal prefixes `amri-modal-`). */
const MODAL_ID = 'study-guide';
const MODAL_ELEMENT_ID = 'amri-modal-study-guide';
const COOKIE_NAME = 'StudyGuidePopupShown';
const COOKIE_DAYS = 30;
/** Legacy `StudyGuidePopupDelayInMiliseconds`. */
const DELAY_MS = 30000;

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days: number): void {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function popupHasBeenShown(): boolean {
    return getCookie(COOKIE_NAME) === 'true';
}

/** True while any modal (auth, study-guide, …) is on screen. */
function anyModalOpen(): boolean {
    return document.body.classList.contains('amri-modal-open');
}

/**
 * Arm the timed + exit-intent auto-open. No-op on pages without the modal, or
 * once the 30-day cookie has been set.
 */
export function initStudyGuideAutoOpen(): void {
    // Opt-in: only auto-open where the page rendered the modal with
    // data-auto-open="true" (legacy fired the popup on exam-prep / quiz pages,
    // NOT on the blog listing — which still renders the modal for its manual
    // "Get the Free Guide" CTA). Gate at most once per 30 days via the cookie.
    const modal = document.getElementById(MODAL_ELEMENT_ID);
    if (modal?.getAttribute('data-auto-open') !== 'true' || popupHasBeenShown()) {
        return;
    }

    let fired = false;
    let timer = 0;

    function cleanup(): void {
        window.clearTimeout(timer);
        document.removeEventListener('mouseout', onExitIntent);
    }

    function show(): void {
        // Never rudely close a modal the visitor already opened (openModal
        // closes others first) — if they're engaged, stay out of the way.
        if (fired || anyModalOpen()) {
            return;
        }
        fired = true;
        cleanup();
        openModal(MODAL_ID);
        setCookie(COOKIE_NAME, 'true', COOKIE_DAYS);
    }

    function onExitIntent(event: MouseEvent): void {
        // Fire only when the cursor genuinely leaves the viewport via the top
        // (relatedTarget null = left the document; clientY <= 0 = upward).
        if (event.relatedTarget !== null || event.clientY > 0) {
            return;
        }
        show();
    }

    timer = window.setTimeout(show, DELAY_MS);
    document.addEventListener('mouseout', onExitIntent);
}
